import { DatePicker } from "antd";
import { translate } from "shared/languages";
import { now, tzDate } from "shared/utils/dayjs";
import { CustomMonthRange } from "shared/types";

const { RangePicker } = DatePicker;

interface CustomMonthRangePickerProps {
  value: CustomMonthRange;
  onChange: (value: CustomMonthRange) => void;
  // Optional inclusive month-count ceiling on the picked span — e.g. 12 means
  // "from" and "to" can be at most 11 months apart. Undefined (the default)
  // keeps every other consumer of this shared picker exactly as it was; only
  // pass this where an unbounded range is a known problem for that specific
  // report (e.g. SalesTrendPage's bar charts, whose x-axis becomes illegible
  // past a year of months).
  maxRangeMonths?: number;
}

// AntD's DatePicker hands callbacks a Dayjs from ITS OWN bundled dayjs
// instance, not ours (see shared/utils/dayjs.ts) — TS treats the two as
// distinct nominal types even though both are plain dayjs under the hood,
// and matching dayjs's own isAfter/isBefore overload signatures structurally
// is brittle. Comparing plain (year, month) numbers sidesteps all of that.
type MonthValue = { format: (fmt: string) => string; year: () => number; month: () => number };

const monthKey = (d: MonthValue) => d.year() * 12 + d.month();

// Month-precision sibling of CustomRangePicker.tsx (the day-precision picker
// every other report uses) — same plain connected RangePicker, same styling,
// just picker="month" and a "MMM YYYY" format/placeholder instead of a plain
// date. A connected RangePicker also gets the maxRangeMonths cap for free
// via disabledDate's `info.from` (the anchor date while picking the SECOND
// side, provided natively by rc-picker) — no independent-fields bookkeeping,
// no hand-rolled "reference cell" hint, and no order-dependent clamping like
// the two-independent-DatePickers version this replaced needed (that
// version's cap only checked a field against whatever the OTHER field held
// AT OPEN TIME, which could be bypassed depending on which side was picked
// first).
const CustomMonthRangePicker = ({ value, onChange, maxRangeMonths }: CustomMonthRangePickerProps) => {
  // Inclusive-count cap expressed as a monthKey difference: maxRangeMonths=12
  // ("from"/"to" at most 11 apart) spans exactly 12 calendar months inclusive.
  const maxSpan = maxRangeMonths !== undefined ? maxRangeMonths - 1 : undefined;

  return (
    // The closed field ("Mei 2026") and the popup's month cells follow the
    // admin's language, like every date on the reports since 2026-10-05 (this
    // picker used to force AntD's English locale to match the reports'
    // then-English months).
    <RangePicker
      picker="month"
      // Matches CustomRangePicker.tsx's fixed (not "100%") width — this
      // picker shares a wrapping flex row with RangeSegmented/
      // TrendMonthsSegmented, and width:"100%" would claim the whole row.
      style={{ width: 260 }}
      format="MMM YYYY"
      placeholder={[
        translate("analyticsPage.sales.custom_range_from"),
        translate("analyticsPage.sales.custom_range_to"),
      ]}
      value={value ? [tzDate(value.from), tzDate(value.to)] : null}
      onChange={(dates) => {
        if (!dates || !dates[0] || !dates[1]) {
          onChange(null);
          return;
        }
        onChange({
          from: dates[0].format("YYYY-MM"),
          to: dates[1].format("YYYY-MM"),
        });
      }}
      disabledDate={(current: MonthValue, info: { from?: MonthValue }) => {
        if (monthKey(current) > monthKey(now())) return true;
        if (maxSpan !== undefined && info.from) {
          return Math.abs(monthKey(current) - monthKey(info.from)) > maxSpan;
        }
        return false;
      }}
    />
  );
};

export default CustomMonthRangePicker;
